import type { SignatureValidationRegime } from '@argink/shared/owner-web';
import {
  ACTION,
  DOCUMENT_FORMAT_LABEL as LEXICON_DOCUMENT_FORMAT_LABEL,
  ENVELOPE_STATUS_DESCRIPTION as LEXICON_ENVELOPE_STATUS_DESCRIPTION,
  ENVELOPE_STATUS_LABEL as LEXICON_ENVELOPE_STATUS_LABEL,
  MODALITY_LABEL as LEXICON_MODALITY_LABEL,
  MODALITY_DESCRIPTION as LEXICON_MODALITY_DESCRIPTION,
  PARTICIPANT_STATUS_LABEL_OWNER,
  TERM,
} from '@argink/shared/surface-lexicon';
import {
  ApiError,
  ContractViolationError,
  ENVELOPE_STATUS_VALUES,
  PARTICIPANT_STATUS_VALUES,
  type CeremonyMode,
  type DocumentFormat,
  type EnvelopeStatus,
  type ParticipantRole,
  type ParticipantStatus,
  type SignatureLevel,
  type VerificationVerdict,
} from './api/types';

/**
 * Copy contratual do mandato (§5/§9/§10/§12) — texto literal, não parafrasear.
 * Ponto único de verdade: nenhuma página escreve esses textos "na mão" de novo.
 * `avancada` nunca aparece aqui — não existe entrada para ela.
 *
 * Os mapas de status são `Record<Enum, …>` sobre os enums REAIS do backend (7
 * de envelope, 6 de participante). A exaustividade em tempo de execução —
 * incluindo o caso "status que este cliente não conhece" — mora em
 * `lib/api/status.ts`; aqui fica só o texto.
 */

// ---------------------------------------------------------------- modalidade

/**
 * Rótulos dos **3** níveis do enum real (`SignatureLevel`). `avancada` está aqui
 * porque pode CHEGAR num envelope legado criado por `/v1` — e um valor que chega
 * precisa de rótulo honesto, não de badge vazio. Ela nunca é oferecida na
 * criação: o wizard percorre `SIGNATURE_MODALITIES` (2 valores), não este mapa.
 */
export const MODALITY_LABEL: Record<SignatureLevel, string> = LEXICON_MODALITY_LABEL;

export const MODALITY_DESCRIPTION: Record<SignatureLevel, string> = LEXICON_MODALITY_DESCRIPTION;

/** Observador não tem modalidade (mandato §5) — texto, nunca ausência de badge. */
export const MODALITY_NONE_LABEL = 'Não se aplica (observador)';

/**
 * `actualModality: null` para um SIGNATÁRIO significa algo diferente de
 * `null` para um observador: não é "não se aplica", é "ainda não assinou".
 * Confundir os dois casos afirma implicitamente que a pessoa é observadora —
 * falso quando é signatário Qualificada esperando a vez (achado de UI, vetor
 * 20: tela mostrando estado diferente do backend). O componente que usa este
 * texto precisa saber o PAPEL do participante, não só a modalidade.
 */
export const MODALITY_PENDING_LABEL = 'Ainda não assinou';

/** "Assinatura Simples — Verificação por e-mail e registro da manifestação" etc.
 * A distinção entre níveis é SEMPRE textual: mandato §5 proíbe diferenciar
 * modalidade apenas por cor. */
export function modalityFullLabel(modality: SignatureLevel): string {
  return `${MODALITY_LABEL[modality]} — ${MODALITY_DESCRIPTION[modality]}`;
}

/** Nível que este cliente não conhece (enum do motor cresceu). Nunca vazio,
 * nunca disfarçado de Simples/Qualificada. */
export const MODALITY_UNKNOWN_LABEL = 'Modalidade não reconhecida';
export const MODALITY_UNKNOWN_DESCRIPTION =
  'Esta versão do aplicativo não sabe interpretar a modalidade informada pelo servidor.';

// ---------------------------------------------------------------- status de envelope (7)

/**
 * R-UX01 §54 — estes rótulos NÃO são mais definidos aqui.
 *
 * Eles viviam duplicados neste arquivo e em `apps/signer-ui/src/lib/format.ts`, e já tinham
 * divergido: o mesmo `ready` era "Pronto para disparo" aqui e "Pronto para iniciar" lá. Duas
 * UIs do mesmo produto dizendo palavras diferentes para a mesma condição é exatamente o que o
 * mandato proíbe. A fonte única agora é `@argink/shared/surface-lexicon`.
 *
 * O nome exportado continua o mesmo de propósito: quem consome não precisou mudar.
 */
export const ENVELOPE_STATUS_LABEL: Record<EnvelopeStatus, string> =
  LEXICON_ENVELOPE_STATUS_LABEL;

export const ENVELOPE_STATUS_DESCRIPTION: Record<EnvelopeStatus, string> =
  LEXICON_ENVELOPE_STATUS_DESCRIPTION;

// ---------------------------------------------------------------- status de participante (6)

/**
 * Perspectiva do DONO — ele lê sobre OUTRA pessoa. O app do signatário usa
 * `PARTICIPANT_STATUS_LABEL_SIGNER`, que fala em segunda pessoa ("Aguardando sua vez").
 * Isso é audiência, não divergência: as duas perspectivas ficam lado a lado no léxico
 * compartilhado, onde a diferença é visível e intencional.
 */
export const PARTICIPANT_STATUS_LABEL: Record<ParticipantStatus, string> =
  PARTICIPANT_STATUS_LABEL_OWNER;

export const PARTICIPANT_STATUS_DESCRIPTION: Record<ParticipantStatus, string> = {
  pending: 'Ainda não recebeu o convite para assinar.',
  notified: 'Recebeu o convite por e-mail e ainda não assinou.',
  in_session: 'Está com a cerimônia de assinatura aberta neste momento.',
  signed: 'Assinatura registrada.',
  declined: 'Recusou assinar este documento.',
  expired: 'O prazo terminou sem a assinatura desta pessoa.',
};

/**
 * Copy do estado que este cliente **não** conhece — um status novo nascido no
 * backend. Existe para que nada renderize vazio nem seja rotulado com um estado
 * plausível porém falso (mandato §9: proibido fallback silencioso).
 */
export const UNKNOWN_STATUS_LABEL = 'Estado não reconhecido';
export const UNKNOWN_STATUS_DESCRIPTION =
  'Esta versão do aplicativo não sabe interpretar o estado informado pelo servidor. Recarregue a página; se continuar, fale com o suporte antes de agir sobre este documento.';

// ---------------------------------------------------------------- ausências honestas

/** Nome do documento pode vir `null` do backend — nunca mostrar `''` nem inventar. */
export const DOCUMENT_WITHOUT_NAME_LABEL = 'Documento sem nome';
export function documentDisplayName(name: string | null | undefined): string {
  const trimmed = (name ?? '').trim();
  return trimmed.length > 0 ? trimmed : DOCUMENT_WITHOUT_NAME_LABEL;
}

/** E-mail de participante pode vir `null` (PII apagável, D-029). */
export const PARTICIPANT_WITHOUT_EMAIL_LABEL = 'E-mail não disponível';
export function participantEmailDisplay(email: string | null | undefined): string {
  const trimmed = (email ?? '').trim();
  return trimmed.length > 0 ? trimmed : PARTICIPANT_WITHOUT_EMAIL_LABEL;
}

/** `expires_at` é `string | null` — envelope sem prazo é estado legítimo. */
export const NO_DEADLINE_LABEL = 'Sem prazo definido';

// ---------------------------------------------------------------- detalhe do envio (R-UX02-P02/L3B-B)

/**
 * Título do Detalhe — TRÊS casos distintos, nunca colapsados: sem documento
 * associado nenhum (`document: null`), documento existe mas sem nome
 * (`document.name: null`), nome real. Os dois últimos já eram resolvidos por
 * `documentDisplayName`; o primeiro usa o mesmo rótulo que a LISTA já usa para
 * a mesma ausência (`ENVELOPE_WITHOUT_DOCUMENT_LABEL`, adiante neste arquivo).
 */
export function envelopeDetailTitle(document: { name: string | null } | null): string {
  return document ? documentDisplayName(document.name) : ENVELOPE_WITHOUT_DOCUMENT_LABEL;
}

/** Rótulo próprio para cada um dos 4 modos de cerimônia (`CeremonyMode`, enum
 * real do Prisma) — nunca o valor cru do enum na tela. */
export const CEREMONY_MODE_LABEL: Record<CeremonyMode, string> = {
  unitary: 'Assinatura única',
  sequential: 'Sequencial — uma etapa de cada vez',
  parallel_quorum: 'Quorum — M de N assinaturas',
  /** B6/Correção B: rótulo próprio, sem explicar a mecânica interna (o modo não
   * é "o mesmo critério de outro modo" para quem lê — é um nome que se basta). */
  batch_async: 'Lote assíncrono',
};

/**
 * Rótulo do formato do documento (`DocumentFormat`) — nunca o enum cru
 * (`pades`/`cades`/`xades`) na tela. Vem do léxico canônico
 * (`@argink/shared/surface-lexicon`), não de um texto próprio: `pades` cru já
 * vazou como "PADES" numa tela deste produto antes (M6-A) e "PAdES" está em
 * `FORBIDDEN_SURFACE_TERMS` — a mesma duplicata que o léxico existe para
 * impedir (ver o cabeçalho do arquivo lá).
 */
export const DOCUMENT_FORMAT_LABEL: Record<DocumentFormat, string> = LEXICON_DOCUMENT_FORMAT_LABEL;

/** Rótulo do papel do participante — `TERM.signer` é canônico (léxico
 * compartilhado); "Observador" não tem entrada lá ainda, mas é o mesmo termo
 * já usado em `EnvelopeNewPage.tsx`/`ParticipantTable.tsx`, nunca reinventado. */
export const PARTICIPANT_ROLE_LABEL: Record<ParticipantRole, string> = {
  signer: TERM.signer,
  observer: 'Observador',
};

/**
 * Copy do Detalhe que não se encaixa nos mapas acima — agrupada num objeto só
 * (mesmo padrão de `RESEND_QUEUED_COPY`/`VALIDATE_RESULT_COPY`) para não
 * multiplicar exports soltos.
 */
export const ENVELOPE_DETAIL_COPY = {
  loadingLabel: 'Carregando envio',
  idLabel: 'ID do envio',
  copyIdLabel: 'Copiar ID do envio',
  copyIdDoneLabel: 'ID copiado',
  /** B7/Correção B: falha do clipboard precisa ficar VISÍVEL, não só anunciada
   * para leitor de tela (`role="status"` sozinho não bastava — texto em
   * `.argink-sr-only` nunca aparecia para quem enxerga a tela). */
  copyIdErrorLabel: 'Não foi possível copiar automaticamente — selecione o ID manualmente.',
  createdAtLabel: 'Criado em',
  updatedAtLabel: 'Atualizado em',
  flowLabel: 'Fluxo',
  quorumLabel: 'Quorum',
  currentStageLabel: 'Etapa atual',
  quorum: (quorum: number, total: number) => `${quorum} de ${total} assinaturas`,
  documentSectionTitle: 'Documento',
  documentFormatLabel: 'Formato',
  documentPageCountLabel: 'Páginas',
  documentPageCountUnknown: 'Quantidade de páginas não registrada',
  documentHashLabel: 'Hash SHA-256',
  documentSignedLabel: 'Arquivo assinado',
  documentSignedAvailable: 'Disponível',
  documentSignedUnavailable: 'Ainda não disponível',
  participantsSectionTitle: 'Participantes',
  channelVerifiedAtLabel: 'Canal confirmado em',
  signedAtLabel: 'Assinou em',
  declineReasonLabel: 'Motivo da recusa',
  actualModalityLabel: 'Modalidade realizada',
  progress: (signed: number, total: number) => `${signed} de ${total} assinaram`,
  /** PARTIAL — refresh/polling falhou, dados anteriores preservados. */
  partialTitle: 'Não foi possível atualizar agora',
  partialDescription: 'Os dados exibidos podem estar desatualizados.',
  retryLabel: 'Tentar de novo',
} as const;

// ---------------------------------------------------------------- veredito

/**
 * PASSED/FAILED/INDETERMINATE → linguagem humana (mandato §10, IMVP-30).
 * `TOTAL_PASSED` nunca é headline — este mapeamento não conhece esse valor.
 */
export function verificationHeadline(
  modality: 'qualificada' | 'mixed',
  _verdict: VerificationVerdict,
): string {
  return modality === 'qualificada'
    ? 'Validação ICP-Brasil pelo ARGINK'
    : 'Evidências verificadas pelo ARGINK';
}

export const VERDICT_LABEL: Record<VerificationVerdict, string> = {
  PASSED: 'Verificada',
  FAILED: 'Falhou',
  INDETERMINATE: 'Indeterminada',
};

export const VERDICT_TONE: Record<VerificationVerdict, 'proof' | 'danger' | 'warning'> = {
  PASSED: 'proof',
  FAILED: 'danger',
  INDETERMINATE: 'warning',
};

/** Nota de limite fixa exibida junto do veredito — nunca deixar "PASSED" sozinho
 * insinuar completude/atribuição de chave sem PKI (AMVP-09/IMVP-27, D-040). */
export const VERDICT_LIMIT_NOTE =
  'A verificação confirma coerência interna da cadeia de evidências sob a chave apresentada — não é, por si só, atribuição jurídica da chave nem prova de completude do histórico.';

/** O relatório é remontado e reverificado a cada chamada: o único instante que o
 * front pode afirmar com verdade é o da resposta que ele recebeu. */
export const VERDICT_RECEIVED_AT_PREFIX = 'Resultado recebido em';

export const VERIFICATION_CHECK_STATUS_LABEL: Record<
  'passed' | 'failed' | 'indeterminate' | 'skipped',
  string
> = {
  passed: 'Conferido',
  failed: 'Falhou',
  indeterminate: 'Indeterminado',
  skipped: 'Não aplicável',
};

// ---------------------------------------------------------------- resultado e evidências

export const RESULT_TITLE = 'Documento concluído';

/**
 * O CONCEITO da tela `/envelopes/:id/result`, que não muda com o estado — ao
 * contrário do título, que muda (`RESULT_TITLE` × `RESULT_PENDING_TITLE`). É
 * daqui que sai o rótulo de quem volta para ela: um link de volta nomeia a tela
 * de destino, e nomear pelo título seria escolher entre dois nomes conforme um
 * estado que o link não conhece.
 */
export const RESULT_SCREEN_NAME = 'Resultado';

/** Mesma rota, envio ainda aberto: o header não pode anunciar conclusão que não
 * houve enquanto o corpo da página avisa o contrário. */
export const RESULT_PENDING_TITLE = `${RESULT_SCREEN_NAME} do envio`;

/**
 * Rótulo ÚNICO do caminho de volta para a Conclusão — mesmo achado do
 * `BACK_TO_OVERVIEW`, um degrau adiante: a Evidência escrevia "Voltar ao
 * resultado" à mão, e nada ligava aquela string ao nome da tela de destino.
 * Renomear a tela renomeia o link.
 */
export const BACK_TO_RESULT = `Voltar ao ${RESULT_SCREEN_NAME.toLowerCase()}`;

/**
 * Caminho para o acompanhamento do envio (`/envelopes/:id`). Estava escrito à
 * mão em DUAS telas — Conclusão e Evidências — com as mesmas palavras e nada
 * comparando as cópias. É a armadilha que o léxico compartilhado já desarmou
 * para modalidade e status: duplicata que hoje coincide é a que ninguém percebe
 * divergir, porque nada falha no dia em que uma das duas muda.
 *
 * O Detalhe não tem título fixo (o header dele é o NOME DO DOCUMENTO), então
 * este rótulo nomeia a função da tela, não o título — e por isso não deriva de
 * nada: ele É a origem.
 */
export const ENVELOPE_FOLLOW_LABEL = 'Ver acompanhamento';
export const RESULT_DOWNLOAD_LABEL = 'Baixar documento assinado';
export const ITI_VALIDATION_LABEL = 'Validar também no ITI';

/**
 * S2/Correção B — o Resultado renderizava Baixar/Validar para TODO envelope
 * `completed`, mesmo com `document: null` ou `document.signed: false`: os dois
 * botões existiam e não faziam nada (o `handleDownload`/`handleValidate`
 * silenciosamente retornavam cedo). Os dois só aparecem quando
 * `document?.signed === true`; nos outros dois casos, este aviso factual ocupa
 * o lugar — nunca um controle inoperante.
 */
export const RESULT_DOCUMENT_UNAVAILABLE_TITLE = 'Documento assinado indisponível';
export const RESULT_DOCUMENT_MISSING_DESCRIPTION =
  'Este envio não tem documento associado — não há o que baixar ou validar aqui.';
export const RESULT_DOCUMENT_NOT_SIGNED_DESCRIPTION =
  'O documento assinado ainda não está disponível para este envio — não há o que baixar ou validar por enquanto.';

// D-063/#100 — ação "Validar documento assinado". Tri-state HONESTO: `indeterminate`
// NUNCA é apresentado como inválido nem como falha do documento; nenhuma variante
// promete aprovação de autoridade ("aprovado pelo ITI") nem validade jurídica.
export const VALIDATE_ACTION_LABEL = 'Validar documento assinado';
export const VALIDATE_EXPLANATION =
  'Validar o documento confere as assinaturas digitais dentro do próprio PDF assinado. ' +
  'É diferente de verificar as evidências, que confere o pacote de evidências da cerimônia ' +
  '(quem assinou, quando, por qual canal). As duas conferências se complementam.';

export interface ValidateResultCopy {
  title: string;
  description: string;
}

export type ValidateResultState = 'valid' | 'invalid' | 'indeterminate';

/**
 * Copy do regime **ICP-Brasil** — só é usada quando a validação realmente percorreu uma cadeia de
 * certificação ICP (`validationRegimeFromIndication(indication) === 'icp_brasil'`). Falar em
 * "cadeia de certificação confirmada" fora daí seria atribuir ao documento uma qualificação que
 * ele não tem (achado P1-2 da auditoria adversarial R1).
 */
export const VALIDATE_RESULT_COPY: Record<ValidateResultState, ValidateResultCopy> = {
  valid: {
    title: 'Assinaturas do PDF validadas',
    description:
      'As assinaturas digitais do documento foram verificadas com sucesso pelo motor de validação: ' +
      'a integridade está preservada e a cadeia de certificação foi confirmada.',
  },
  invalid: {
    title: 'A validação reprovou o documento',
    description:
      'O motor de validação encontrou um problema nas assinaturas deste PDF. Baixe o pacote de ' +
      'evidências e acione o suporte — não distribua este arquivo como prova antes de esclarecer.',
  },
  indeterminate: {
    title: 'Não foi possível confirmar toda a cadeia',
    description:
      'A assinatura confere com o documento, mas o motor não conseguiu confirmar toda a cadeia de ' +
      'certificação agora (por exemplo, a consulta de revogação pode estar indisponível). Isto NÃO ' +
      'significa que o documento é inválido — tente novamente mais tarde ou confira no Verificador do ITI.',
  },
};

/**
 * Copy do regime **selo interno da plataforma** (Assinatura Simples, D-069).
 *
 * O selo interno é emitido sob uma raiz privada dedicada do ARGINK: **não é ICP-Brasil**, não tem
 * perfil AD-R* e não há cadeia de certificação ICP a confirmar. A tela nomeia o que foi conferido
 * — integridade do documento sob o selo — e diz explicitamente o que NÃO foi, em vez de repetir a
 * frase da Qualificada e deixar o leitor concluir errado.
 */
export const VALIDATE_RESULT_COPY_INTERNAL_SEAL: Record<ValidateResultState, ValidateResultCopy> = {
  valid: {
    title: 'Selo interno da plataforma validado',
    description:
      'O selo interno do ARGINK foi verificado com sucesso pelo motor de validação: a integridade ' +
      'do documento está preservada sob o selo. Este documento é de Assinatura Simples — o selo é ' +
      'emitido pela própria plataforma, não é um certificado ICP-Brasil, e por isso não há cadeia ' +
      'de certificação ICP-Brasil confirmada aqui.',
  },
  invalid: {
    title: 'A validação reprovou o selo interno',
    description:
      'O motor de validação encontrou um problema no selo interno deste PDF. Baixe o pacote de ' +
      'evidências e acione o suporte — não distribua este arquivo como prova antes de esclarecer.',
  },
  indeterminate: {
    title: 'Não foi possível confirmar o selo interno',
    description:
      'O motor não conseguiu confirmar o selo interno deste documento agora. Isto NÃO significa ' +
      'que o documento é inválido — tente novamente mais tarde. Como este documento é de ' +
      'Assinatura Simples, não há cadeia de certificação ICP-Brasil envolvida nesta conferência.',
  },
};

/**
 * Copy do veredito da validação **derivada do regime** de confiança, nunca do nível declarado do
 * envelope (que pode ser misto). O regime chega do backend na própria `indication`
 * (`validationRegimeFromIndication`, contrato `@argink/shared/owner-web`).
 */
export function validateResultCopy(
  regime: SignatureValidationRegime,
  result: ValidateResultState,
): ValidateResultCopy {
  return regime === 'internal_seal'
    ? VALIDATE_RESULT_COPY_INTERNAL_SEAL[result]
    : VALIDATE_RESULT_COPY[result];
}

/**
 * Camada humana da Evidência (§13 do M6-D) — o que a tela responde ANTES do veredito.
 * "Assinaturas registradas" e não "Resumo": a página inteira é sobre registro, e um título
 * que só anuncia que há um resumo não acrescenta o que o bloco de fato afirma.
 */
export const EVIDENCE_SUMMARY_TITLE = 'Assinaturas registradas';
export const EVIDENCE_SUMMARY_DOCUMENT_LABEL = 'Documento';
/** Ausência declarada, nunca lista vazia silenciosa. */
export const EVIDENCE_SUMMARY_EMPTY = 'Nenhuma assinatura consta neste registro.';
/** O instante existe no modelo mas pode não ter chegado — dizer "—" esconderia a lacuna. */
export const EVIDENCE_SUMMARY_UNKNOWN_INSTANT = 'data não registrada';
/** Participante que não assinou não é evidência; a contagem evita que a lista curta pareça
 * a lista inteira do envio. */
export const EVIDENCE_SUMMARY_NOT_SIGNED = (quantos: number) =>
  quantos === 1
    ? 'Outro participante do envio não assinou.'
    : `Outros ${quantos} participantes do envio não assinaram.`;

export const EVIDENCE_DOWNLOAD_BUNDLE_LABEL = 'Baixar pacote de evidências';
export const EVIDENCE_VIEW_MANIFEST_LABEL = 'Ver manifesto';
export const EVIDENCE_VIEW_DETAILS_LABEL = 'Ver detalhes da validação';

/**
 * D-109 (R-UX02-P02/L3C) — `capabilities.evidence_delivery === false` na sessão:
 * o ambiente não entrega evidências, e a tela precisa dizer isso sem fingir uma
 * indisponibilidade temporária (que pediria "tente novamente") nem citar termos
 * de configuração que não dizem respeito a quem está enviando o documento.
 */
export const EVIDENCE_UNAVAILABLE_TITLE = 'Evidências indisponíveis neste ambiente';
export const EVIDENCE_UNAVAILABLE_DESCRIPTION =
  'A entrega do pacote de evidências deste envio não está habilitada aqui.';

export const PARTICIPANT_TABLE_HEADERS = [
  'Nome',
  'Papel',
  'Modalidade exigida',
  'Modalidade realizada',
  'Status',
  'Data',
] as const;

export const STAGE_MODE_LABEL = {
  free: 'Todos podem assinar em qualquer ordem',
  staged: 'Definir etapas',
} as const;

// ---------------------------------------------------------------- superfície do tenant

/**
 * **A palavra do objeto é "Envio", e ela não foi escolhida aqui.**
 *
 * O léxico canônico (`@argink/shared/surface-lexicon`, R-UX01 §23) já tinha fixado
 * `TERM.send = 'Envio'` e `TERM.newSend = 'Novo envio'` como a tradução de superfície
 * de `envelope`, e os dois SPAs consomem esse mapa. O contrato desta sessão (§28) manda
 * escolher UM nome para o conceito e proíbe "novo envio"/"novo processo"/"nova
 * solicitação" convivendo — então a escolha é a autoridade que já está instalada, não
 * uma terceira palavra.
 *
 * O que muda é que ESTA tela para de divergir dela: o CTA dizia "Novo documento", que
 * nomeia o arquivo (`TERM.document`) e não o objeto que se cria. Um envio tem um
 * documento; não é um.
 */
export const NEW_ENVELOPE_LABEL = TERM.newSend;

/** @deprecated Divergia do léxico (`TERM.document` ≠ `TERM.send`). Alias mantido só
 * para nada quebrar em silêncio; usar `NEW_ENVELOPE_LABEL`. */
export const DASHBOARD_NEW_DOCUMENT_LABEL = NEW_ENVELOPE_LABEL;

/** Rótulo da sidebar para `/dashboard` — DIFERENTE do título da própria página
 * (`OVERVIEW_TITLE`, "Visão geral"). É o mandato de nav do R-UX02-P01/D-101: o
 * item da sidebar chama "Painel", a página que ele abre continua se chamando
 * "Visão geral" no seu próprio `<h1>` — igual a "Dashboard" abrir uma página
 * intitulada de outro jeito em muitos produtos; não é divergência de léxico,
 * é rótulo de MENU × título de TELA, papéis diferentes. */
export const NAV_PAINEL_LABEL = 'Painel';

/**
 * Navegação REAL do produto (R-UX02-P01/D-101) — só os dois destinos com rota E
 * runtime vivos hoje. Isto SUBSTITUI a antiga `TENANT_NAV` (quatro itens, glifo
 * Unicode como ícone): "Novo envio" virou CTA da topbar (não duplica aqui),
 * "Configurações" ficou fora da nav (`/settings` existe, "Conta" está fora desta
 * fatia — D-100), e nenhum item sem runtime (Modelos, Contatos, Relatórios,
 * Ferramentas, Certificados, API/webhooks, Consumo) entra: ausência declarada
 * vale mais que completude visual copiada de mockup (§11).
 *
 * `icon` é uma CHAVE, não o componente Lucide em si — este arquivo é dado puro
 * (nenhum import de React/lucide-react), e quem resolve a chave para o ícone
 * real é `components/layout/TenantShell.tsx`, que já depende de React mesmo.
 */
export const PRODUCT_NAV: readonly {
  to: string;
  label: string;
  icon: 'dashboard' | 'envelopes';
  /** `end` evita que `/envelopes` fique ativo enquanto se está em `/envelopes/new`. */
  end?: boolean;
}[] = [
  { to: '/dashboard', label: NAV_PAINEL_LABEL, icon: 'dashboard', end: true },
  /* Literal 'Envios', e não `ENVELOPES_TITLE` — aquela constante só é declarada
   * mais abaixo neste mesmo arquivo (§24), e referenciá-la aqui quebraria em
   * "Cannot access before initialization" (TDZ de `const` top-level, ordem de
   * execução do módulo). É a MESMA palavra por decisão, não por acidente: o
   * teste de nav confere as duas contra o mesmo texto. */
  { to: '/envelopes', label: 'Envios', icon: 'envelopes' },
];

// ---------------------------------------------------------------- "ir para…" (R-UX02-P01/D-101)

/** Nome do controle na sidebar E título do Dialog — mesma string, mesmo motivo de
 * sempre (§11: um conceito, um nome). Não é "busca de documentos": as únicas três
 * superfícies encontráveis são Painel, Envios e Novo envio (mandato §4 do
 * contrato de dados desta rodada, D-100). */
export const GO_TO_LABEL = 'Ir para…';
/** Rótulo do campo — acessível (associado ao `<input>`), nunca visível como
 * placeholder de "buscar documentos": aqui não se busca documento nenhum. */
export const GO_TO_FIELD_LABEL = 'Filtrar destinos';
export const GO_TO_PLACEHOLDER = 'Painel, Envios…';
export const GO_TO_EMPTY_STATE = 'Nenhum destino encontrado com esse termo.';
/** Rótulo do atalho, exibido junto do controle — funciona com Ctrl OU Cmd; o
 * texto nomeia os dois porque o teclado físico do visitante não é conhecido. */
export const GO_TO_SHORTCUT_LABEL = 'Ctrl/Cmd+K';

// ---------------------------------------------------------------- visão geral (§15/§16)

export const OVERVIEW_TITLE = 'Visão geral';

/**
 * Rótulo ÚNICO do caminho de volta para `/dashboard`. Quatro telas escreviam "Voltar ao painel"
 * enquanto a tela de destino se chama "Visão geral" (e o próprio brand link diz "visão geral"):
 * um conceito com dois nomes, que é exatamente o que o §11 do M6 proíbe. O rótulo passa a ser
 * derivado de OVERVIEW_TITLE — renomear a tela renomeia o link, sem caçar string solta.
 */
export const BACK_TO_OVERVIEW = `Voltar à ${OVERVIEW_TITLE.toLowerCase()}`;
export const OVERVIEW_SUBTITLE = 'Acompanhe seus envios para assinatura.';

/**
 * Rótulos dos indicadores. São TRÊS porque são três as perguntas que o §8 manda a
 * tela responder — não há quarto cartão para preencher a linha.
 */
export const OVERVIEW_METRIC_LABEL = {
  waiting: 'Aguardando assinatura',
  completed: 'Concluídos',
  attention: 'Requer atenção',
} as const;

export const OVERVIEW_METRIC_DESCRIPTION = {
  waiting: 'Convites enviados, assinaturas ainda pendentes.',
  // Mesma correção do léxico compartilhado (P1-1): sob `parallel_quorum` o backend conclui
  // com M de N, e "todos assinaram" descreveria o cartão inteiro de forma falsa.
  completed: 'Envios concluídos — o documento final está disponível.',
  attention: 'Recusa, prazo ou envio parado esperando você.',
} as const;

/**
 * O BFF **não expõe contagem**: `/web/envelopes` devolve uma página e um cursor, e
 * mais nada. Estes números são contados sobre os envios efetivamente carregados, e
 * quando existe página seguinte a tela diz isso em vez de apresentar um parcial como
 * se fosse o total (§17/§19 — o produto só afirma o que consegue provar).
 */
/**
 * **A janela é por DATA DE CRIAÇÃO, e isso muda o que a tela pode dizer.**
 *
 * `GET /web/envelopes` ordena por `created_at desc`
 * (`apps/api/src/domain/owner-envelope/service.ts` → `orderBy: [{ createdAt: 'desc' }…]`),
 * então a página que a Visão geral carrega são os N envios mais NOVOS — não os mais
 * movimentados. Um envio criado há meses e recusado ontem fica **fora** dessa janela.
 *
 * Consequência prática: com página seguinte disponível, nada aqui pode ser afirmado
 * sobre o tenant inteiro — nem os números, nem a ausência de pendências, nem qual foi
 * a última movimentação. Por isso a copy muda de forma quando `partial`, em vez de
 * apenas acrescentar um "+" nos cartões e seguir falando no absoluto.
 */
/**
 * `OVERVIEW_ATTENTION_TITLE` segue viva porque `components/home/DashboardPreview.tsx`
 * (ilustração ESTÁTICA da Home pública, fora do ownership desta lane) ainda a usa —
 * é decoração de marketing, não a tela real. As irmãs desta constante que só a
 * Home autenticada usava (nota de parcial, vazios de atenção/recente, títulos com
 * variante "parcial") saíram daqui: aquele recorte parcial não existe mais — ver
 * `DASHBOARD_*` abaixo, que fala do read model exato de `GET /web/dashboard`
 * (R-UX02-P01/L3B).
 */
export const OVERVIEW_ATTENTION_TITLE = 'Requer sua atenção';

/**
 * Idem: `EnvelopesPage.tsx` (lista paginada, fora do ownership desta lane) ainda usa
 * este título de erro genérico para a PRÓPRIA falha de `GET /web/envelopes` — ficou
 * aqui por ela. A Home autenticada tem o seu próprio, `DASHBOARD_ERROR_TITLE`.
 */
export const OVERVIEW_ERROR_TITLE = 'Não foi possível carregar seus envios';

// ---------------------------------------------------------------- home autenticada real (R-UX02-P01/L3B)

/**
 * Copy da Home sustentada por `GET /web/dashboard` (contrato L3B). Nome distinto
 * de propósito (`DASHBOARD_*`, nunca `OVERVIEW_*`): as constantes acima
 * continuam vivas para `DashboardPreview.tsx`/`EnvelopesPage.tsx`, que não são
 * ownership desta lane — reaproveitar o mesmo nome para um contrato diferente
 * teria sido a mesma mistura que a D-100 já teve que corrigir uma vez.
 */

/**
 * "Olá, Marina." — nome REAL da sessão (`session.user.name`), nunca hardcoded.
 * Sem relógio implícito: nada de "Bom dia"/"Boa tarde", que exigiria uma hora
 * que este componente não tem contrato para afirmar. Primeiro nome só — o
 * mandato pede uma forma "estável", e o nome completo de sessões OTP de dev
 * (`"Owner ACME (DEV)"`) não é o tom pretendido. Nome vazio (não deveria
 * acontecer — `sessionInfoDtoSchema` exige `string`, mas pode ser `''`) cai
 * numa saudação sem nome, nunca em "Olá, ." com ponto solto.
 */
export function dashboardGreeting(name: string): string {
  const firstName = name.trim().split(/\s+/)[0] ?? '';
  return firstName ? `Olá, ${firstName}.` : 'Olá.';
}

/** Resumo do que exige atenção — a PRIMEIRA coisa que a tela afirma sobre o
 * tenant, antes até dos quatro indicadores (mandato: "o que exige atenção"
 * vem em primeiro lugar). Os três casos são texto literal do contrato. */
export function dashboardAttentionSummary(attentionCount: number): string {
  if (attentionCount <= 0) return 'Nada requer sua atenção agora.';
  if (attentionCount === 1) return '1 envio requer sua atenção.';
  return `${attentionCount} envios requerem sua atenção.`;
}

/**
 * Rótulos dos QUATRO indicadores, na ORDEM do contrato (Em andamento · Vencendo
 * em 48h · Concluídos · Requer atenção) — autoridade exata de `GET /web/dashboard`,
 * nenhum recalculado no cliente. `completed` é **all-time**: não existe
 * `completedAt` no envelope, e "concluídos no mês" seria uma janela que este
 * endpoint não sustenta (mandato: "proibido 'no mês'").
 */
export const DASHBOARD_METRIC_LABEL = {
  inProgress: 'Em andamento',
  dueWithin48h: 'Vencendo em 48h',
  completed: 'Concluídos',
  attention: 'Requer atenção',
} as const;

export const DASHBOARD_METRIC_DESCRIPTION = {
  inProgress: 'Convite enviado, assinatura ainda pendente.',
  dueWithin48h: 'Em andamento, com prazo terminando em até 48 horas.',
  completed: 'Documento final disponível — total desde o início, sem recorte de período.',
  attention: 'Recusa, prazo vencendo ou vencido, ou envio parado esperando você.',
} as const;

/**
 * Motivo de cada linha da fila de atenção — mapeamento EXAUSTIVO dos 6 `kind`
 * (`Record` total: um `kind` novo não valida no adapter antes de chegar aqui,
 * então não existe "motivo desconhecido" a escrever). Nunca o enum cru na tela
 * (mandato) — é sempre esta frase.
 */
export const DASHBOARD_ATTENTION_HEADLINE: Record<
  'declined' | 'expired' | 'overdue' | 'dueSoon' | 'ready' | 'stalled',
  string
> = {
  declined: 'Um participante recusou assinar.',
  expired: 'O prazo terminou antes de todos assinarem.',
  overdue: 'O prazo venceu e o envio ainda está aberto.',
  dueSoon: 'O prazo termina em menos de 48 horas.',
  ready: 'Pronto para envio — nenhum convite saiu ainda.',
  stalled: 'Sem atividade relevante há pelo menos 7 dias.',
};

/** Rótulo do instante exibido — "Prazo" quando `atKind` é `expiresAt`, "Última
 * atividade" quando é `updatedAt` (mandato §"SEMÂNTICA"). */
export const DASHBOARD_ATTENTION_WHEN_LABEL: Record<'expiresAt' | 'updatedAt', string> = {
  expiresAt: 'Prazo',
  updatedAt: 'Última atividade',
};

/** Ação ÚNICA de cada linha da fila — sempre o acompanhamento (`/envelopes/:id`),
 * nunca `envelopeHref()`: o contrato desta fila é abrir o acompanhamento, mesmo
 * para recusado/expirado, que `envelopeHref()` desviaria para o resultado. */
export const DASHBOARD_ATTENTION_ACTION_LABEL = 'Abrir envio';

/** Retry REAL (`query.refetch()`, classe D do mandato) — visível também fora de
 * erro/parcial: é o mesmo botão que, clicado depois de uma falha de rede,
 * produz o estado PARTIAL (mandato: "retry real"). */
export const DASHBOARD_REFRESH_LABEL = 'Atualizar';

export const DASHBOARD_ATTENTION_TITLE = 'Requer sua atenção';

/** A métrica pode superar os itens mostrados — dito honestamente, sem "estas
 * são todas". Só aparece quando isso é verdade (`hasMoreAttentionThanShown`). */
export const DASHBOARD_ATTENTION_MORE_HINT =
  'Mostra até 5 prioridades — pode haver mais além destas.';

/**
 * R-UX02-P01/L3B-FIX (G3B P2) — quando `metrics.attention === 0` a fila vem
 * vazia (`attentionItems: []`), mas a seção continua existindo (título "Requer
 * sua atenção" fica de pé mesmo com recentes na tela abaixo). Sem esta frase o
 * corpo ficava vazio sob um título — estado incompleto, nunca visto no mandato
 * como aceitável. Mesma frase de `dashboardAttentionSummary(0)`, de propósito:
 * a seção reforça, não inventa, o que o resumo já afirmou lá em cima.
 */
export const DASHBOARD_ATTENTION_EMPTY = 'Nada requer sua atenção agora.';

export const DASHBOARD_RECENT_TITLE = 'Movimentação recente';
export const DASHBOARD_RECENT_EMPTY = 'Nenhum envio movimentado ainda.';

/** Link ADICIONAL (nunca substitui o destino normal da linha) — só em
 * `completed` (`showsEvidenceLink`, `lib/dashboard.ts`). Mesma palavra que
 * `EnvelopeDetailPage`/`EnvelopeResultPage` já usam para a mesma ação. */
export const DASHBOARD_RECENT_EVIDENCE_LABEL = 'Ver evidências';

export const DASHBOARD_LOADING_METRICS_LABEL = 'Carregando indicadores';
export const DASHBOARD_LOADING_ATTENTION_LABEL = 'Carregando o que requer atenção';
export const DASHBOARD_LOADING_RECENT_LABEL = 'Carregando movimentação recente';

/** Falha INICIAL, sem snapshot em cache — mensagem segura, sem stack/corpo cru. */
export const DASHBOARD_ERROR_TITLE = 'Não foi possível carregar sua visão geral';

/**
 * PARTIAL — existe snapshot em cache, mas o refetch falhou. `generatedAtLabel`
 * já vem formatado (timezone do navegador, `formatInstant`) — esta função só
 * monta a frase, nunca lê relógio nem formata data.
 */
export const DASHBOARD_PARTIAL_TITLE = 'Não foi possível atualizar agora';
export const DASHBOARD_PARTIAL_NOTICE = (generatedAtLabel: string) =>
  `Mostrando os últimos dados carregados, de ${generatedAtLabel} — podem estar desatualizados.`;

/**
 * 403 — reservado (não existe hoje modelo de permissão que produza isto numa
 * sessão de owner válida, L2-DATA/§5 INVARIANTES). Estado explícito mesmo
 * assim: sem redirecionar, sem inventar papel/permissão que não existe.
 */
export const DASHBOARD_FORBIDDEN_TITLE = 'Este painel não está disponível para esta sessão';
export const DASHBOARD_FORBIDDEN_DESCRIPTION =
  'Não foi possível confirmar acesso a este conteúdo agora. Se você acredita que deveria ver este painel, fale com o suporte.';

// ---------------------------------------------------------------- lista de envios (§24)

export const ENVELOPES_TITLE = 'Envios';
export const ENVELOPES_SUBTITLE = 'Todos os envios para assinatura deste espaço.';

/**
 * Busca GLOBAL (R-UX02-P02/L3A-2b) — `GET /web/envelopes` aceita `q` e varre TODOS
 * os envios do tenant, não só a página carregada. Copy exata do contrato: nunca
 * mencionar "itens carregados"/"nesta página" — foi exatamente essa mentira de
 * alcance que a busca local (removida nesta rodada) cometia.
 */
export const ENVELOPES_SEARCH_LABEL = 'Buscar envios';
export const ENVELOPES_SEARCH_PLACEHOLDER = 'Documento, participante ou ID';
export const ENVELOPES_SEARCH_HINT = 'A busca considera todos os envios deste espaço.';

/** NO_RESULTS — filtro (`q` e/ou `status`) ativo, existe algo no tenant
 * (`total > 0`), mas nada bate com o filtro (`total_filtered === 0`). Preserva os
 * controles na tela e oferece limpar o filtro, nunca finge tenant vazio. */
export const ENVELOPES_NO_RESULTS_TITLE = 'Nenhum envio encontrado com este filtro';
export const ENVELOPES_NO_RESULTS_DESCRIPTION =
  'Revise o texto buscado ou o status escolhido, ou limpe os filtros para ver todos os envios.';
export const ENVELOPES_CLEAR_FILTERS_LABEL = 'Limpar filtros';

/** PARTIAL — havia uma resposta válida antes e o refetch (nova página, novo filtro
 * ou "Atualizar") falhou; os dados antigos continuam na tela, com este aviso. */
export const ENVELOPES_PARTIAL_TITLE = 'Não foi possível atualizar agora';
export const ENVELOPES_PARTIAL_DESCRIPTION =
  'Não foi possível atualizar a lista. Os dados exibidos podem estar desatualizados.';

/** 403 — copy PRÓPRIA desta lista, nunca o texto de "não encontrado" (`not_found`
 * é sobre um recurso específico; isto é sobre a sessão não poder ver a lista). */
export const ENVELOPES_FORBIDDEN_TITLE = 'Esta lista não está disponível para esta sessão';
export const ENVELOPES_FORBIDDEN_DESCRIPTION =
  'Não foi possível confirmar acesso a este conteúdo agora. Se você acredita que deveria ver esta lista, fale com o suporte.';

/**
 * Contagens autoritativas (R-UX02-P02/L3A-2b) — sempre os três campos que o BFF
 * devolve na MESMA resposta, nunca `items.length` no lugar de `total`/`total_filtered`
 * (era exatamente essa substituição que fazia "20 de 20" mentir sobre um tenant
 * com 527 envios).
 */
export function envelopesCountSummary(totalReturned: number, total: number): string {
  return `Exibindo ${totalReturned} de ${total} envios`;
}

export function envelopesFilteredCountSummary(totalReturned: number, totalFiltered: number): string {
  return `Exibindo ${totalReturned} de ${totalFiltered} resultados`;
}

export function envelopesGrandTotalHint(total: number): string {
  return `${total} envios no total`;
}

/** Documento da lista (R-UX02-P02/L3A-2b) — o TERCEIRO caso que `document_id`
 * existe para distinguir: `documentDisplayName` (acima) já cobre "tem documento,
 * sem nome"; este cobre "não tem documento nenhum" — nunca o mesmo texto. */
export const ENVELOPE_WITHOUT_DOCUMENT_LABEL = 'Envio sem documento associado';

/** Rótulo da coluna final da lista — DIFERENTE de `ENVELOPES_TABLE_HEADERS[4]`
 * ("Última movimentação", que o Dashboard continua usando com `updatedAt`). A
 * lista mostra `last_activity_at` (`max(Envelope.updatedAt, Signer.updatedAt)`),
 * que é uma autoridade mais ampla — e por isso tem o próprio rótulo. */
export const ENVELOPES_LAST_ACTIVITY_LABEL = 'Última atividade';

export const ENVELOPES_TABLE_CAPTION = 'Envios para assinatura';
/**
 * Ordem deliberada: **Estado vem logo depois do nome**. Num viewport estreito a
 * tabela rola dentro do próprio contêiner, e o que sobra à vista são as duas
 * primeiras colunas — se "Assinaturas" ocupasse esse lugar, a pergunta operacional
 * do §8 ("o que está acontecendo com este envio?") ficaria do lado invisível.
 */
export const ENVELOPES_TABLE_HEADERS = [
  'Documento',
  'Estado',
  'Assinaturas',
  'Prazo',
  'Última movimentação',
] as const;

export const DASHBOARD_EMPTY_TITLE = 'Nenhum envio por aqui ainda';
export const DASHBOARD_EMPTY_DESCRIPTION =
  'Envie o primeiro documento para começar a acompanhar as assinaturas.';

// ---------------------------------------------------------------- ações do envelope

/**
 * Rótulos das ações do envelope. Vivem aqui porque as MESMAS ações aparecem em
 * telas diferentes — `start` no fim do wizard e no acompanhamento de um
 * rascunho, por exemplo — e duas telas escrevendo o mesmo botão à mão é como
 * nascem divergências de vocabulário. Quem decide SE a ação existe continua
 * sendo `lib/api/status.ts` (`descriptor.actions`), nunca a página.
 *
 * `start` e `resend` consomem `ACTION` de `@argink/shared/surface-lexicon`
 * (M4-2 WP1, §23) em vez de duplicar a string aqui: era exatamente essa
 * duplicata — "Disparar cerimônia"/"Reenviar notificação" divergindo do léxico
 * canônico — que o mandato tira da superfície comum. `cancel` não tem entrada
 * em `ACTION` (o léxico compartilhado não modela essa ação), então o rótulo é
 * literal, mas com a palavra canônica do objeto (`TERM.send` = "Envio"), nunca
 * "documento".
 */
export const ENVELOPE_START_LABEL = ACTION.send;
export const ENVELOPE_CANCEL_LABEL = 'Cancelar envio';
export const ENVELOPE_RESEND_LABEL = ACTION.resendInvite;

/**
 * O backend responde **202 `queued`** ao reenvio: ele confirma que o convite
 * entrou na fila, não que o e-mail chegou. A copy diz exatamente isso — anunciar
 * "convite enviado" seria afirmar uma entrega que esta tela não tem como saber
 * (mandato §9: nenhuma tela afirma o que não conhece).
 */
export const RESEND_QUEUED_COPY = {
  title: 'Reenvio solicitado',
  description:
    'O convite entrou na fila de envio. O servidor confirma o enfileiramento, não a entrega — o estado do participante muda para "Convite enviado" assim que o e-mail sai.',
} as const;

export const OPEN_TEST_INBOX_LABEL = 'Abrir caixa de teste';

/**
 * **Fallbacks de erro das ações do detalhe — no léxico canônico (§23).**
 *
 * Eles ficavam escritos à mão dentro de `EnvelopeDetailPage.tsx`, em jargão de
 * processo que o §23 tira da superfície: "reenviar a notificação" (o objeto é o
 * **convite**), "disparar a cerimônia" (a ação é `ACTION.send` = "Enviar para
 * assinatura") e "cancelar este documento" (o que se cancela é o **envio**,
 * `TERM.send`, não o arquivo). O gate de léxico não alcançava esses textos
 * porque eles só aparecem quando a ação FALHA — caminho que nenhum teste de
 * superfície renderizava.
 *
 * Cada um é o `fallbackTitle` de `describeApiError`: **não afirma causa**, diz
 * qual ação da tela não passou. A descrição continua vindo da copy central.
 */
export const ACTION_ERROR_TITLE = {
  resend: 'Não foi possível reenviar o convite agora',
  start: 'Não foi possível enviar para assinatura agora',
  cancel: 'Não foi possível cancelar este envio agora',
  load: 'Não foi possível carregar este envio',
} as const;

/**
 * Evento terminal da trilha quando alguém recusou. Dizia "Cerimônia encerrada
 * por recusa": "cerimônia" é a mecânica interna, e o objeto que a linha do tempo
 * acompanha é o **envio** (`TERM.send`) — a mesma palavra do primeiro evento
 * ("Envio criado"). Trocar o nome do objeto no meio da própria trilha é a
 * divergência de vocabulário que o §23 proíbe.
 */
export const TIMELINE_DECLINED_LABEL = `${TERM.send} encerrado por recusa`;

// ---------------------------------------------------------------- entrega (M4-2 WP1/WP2)

/**
 * Copy do passo **Entrega** do wizard. Nasce aqui no WP1 porque texto central
 * não se escreve duas vezes — o componente do passo é responsabilidade do WP2
 * (M4-2), que só consome estas constantes.
 *
 * Hoje existe UM canal operacional: e-mail. WhatsApp aparece na tela como
 * opção visível, porém indisponível, com o motivo explícito — nunca some do
 * seletor nem some sem explicação, e a copy não afirma nem insinua envio,
 * entrega ou leitura por WhatsApp (nada existe para afirmar: não há
 * integração).
 */
export const DELIVERY_COPY = {
  stepTitle: 'Entrega',
  channelLabel: 'Canal de entrega',
  email: {
    label: 'E-mail',
    description: 'Cada participante recebe o convite de assinatura por e-mail.',
  },
  whatsapp: {
    label: 'WhatsApp',
    unavailableReason: 'Ainda não está disponível. Hoje todo convite sai por e-mail.',
  },
} as const;

// ---------------------------------------------------------------- CPF (mandato §10/§11)

export const CPF_FIELD_LABEL = 'CPF';
export const CPF_HINT_QUALIFIED = 'Obrigatório na Assinatura Qualificada.';

/**
 * Texto literal do §11 do mandato. O CPF informado aqui é **autodeclarado** pelo
 * responsável pelo envio: entra na evidência como declaração registrada. A copy
 * NUNCA pode afirmar identidade verificada, conferência documental ou KYC — não
 * existe verificação de identidade por trás deste campo, e prometer uma seria
 * uma afirmação falsa com efeito probatório.
 */
export const CPF_HINT_SIMPLE =
  'Opcional. Quando informado, reforça a identificação registrada nas evidências.';

export const CPF_SELF_DECLARED_NOTE =
  'CPF informado por você e registrado nas evidências como declaração — o ARGINK não faz conferência de identidade neste campo.';

export const CPF_ERROR_COPY = {
  required: 'Informe o CPF: ele é obrigatório na Assinatura Qualificada.',
  format: 'CPF deve ter 11 dígitos.',
  checkDigit: 'CPF inválido — confira os números digitados.',
  observerNotAllowed: 'Observador não assina e por isso não tem CPF neste documento.',
  /** O carimbo mostra o CPF mascarado: sem CPF não há o que desenhar (ver STAMP_*). */
  requiredForStamp: 'Informe o CPF deste signatário ou desligue o carimbo: o carimbo exibe o CPF mascarado.',
} as const;

// ---------------------------------------------------------------- carimbo visível (D-027)

/**
 * **Copy do carimbo — descreve o que o motor DESENHA, nunca mais do que isso.**
 *
 * O conteúdo é fixo e vem de `apps/api/src/domain/visual-stamp.ts` (`buildStampText`): três
 * linhas — "Assinado digitalmente por", o nome em caixa alta e o CPF mascarado — mais a data,
 * acrescentada pelo motor com a `signingDate` da própria sessão de assinatura. Não há
 * personalização de texto, fonte ou tamanho, e a copy não pode insinuar que haja.
 *
 * O carimbo é **aparência**: ele não é a assinatura nem muda a modalidade de ninguém (a prova
 * continua sendo a assinatura e as evidências). Por isso o texto de apoio fala em "marca
 * visível no PDF", jamais em "validade", "autenticidade" ou nível de assinatura.
 */
export const STAMP_OPT_IN_LABEL = 'Carimbo visível no documento';

export const STAMP_OPT_IN_HINT =
  'Desenha no PDF uma marca com o nome do signatário, o CPF mascarado e a data da assinatura. O conteúdo é fixo — não é personalizável. Sem marcar, o documento é assinado sem carimbo visível.';

export const STAMP_PAGE_LABEL = 'Página do carimbo';
export const STAMP_PAGE_HINT = 'Contada a partir de 1, como no leitor de PDF.';
export const STAMP_POSITION_LABEL = 'Posição na página';

export const STAMP_PRESET_LABEL = {
  'bottom-left': 'Rodapé — esquerda',
  'bottom-center': 'Rodapé — centro',
  'bottom-right': 'Rodapé — direita',
  'top-left': 'Topo — esquerda',
  'top-center': 'Topo — centro',
  'top-right': 'Topo — direita',
} as const;

/** Marca discreta na tabela/revisão: diz que o participante TERÁ carimbo, e onde. */
export const STAMP_BADGE_LABEL = 'Carimbo visível';

export function stampBadgeTitle(presetLabel: string, page: number): string {
  return `${STAMP_BADGE_LABEL}: ${presetLabel}, página ${page}`;
}

// ---------------------------------------------------------------- erros da API

/**
 * Códigos que o backend `/web/*` realmente emite (`{ error, message }`).
 * Título + descrição em português — a mensagem crua do servidor nunca vai
 * direto para a tela.
 */
export const API_ERROR_COPY: Record<string, { title: string; description: string }> = {
  validation_error: {
    title: 'Alguns dados precisam de ajuste',
    description: 'Revise os campos destacados e tente de novo.',
  },
  not_found: {
    title: 'Documento não encontrado',
    description: 'Ele pode ter sido removido, ou você não tem acesso a ele.',
  },
  idempotency_conflict: {
    title: 'Esta ação já foi enviada com outros dados',
    description:
      'Uma tentativa anterior usou a mesma identificação com um conteúdo diferente. Recarregue a página e refaça a ação.',
  },
  stale_envelope: {
    title: 'O documento mudou enquanto você trabalhava nele',
    description: 'Recarregue para ver a versão mais recente antes de continuar.',
  },
  /**
   * 409 do reenvio. O backend recusa por DOIS motivos e não distingue qual no
   * corpo (`resolveResendTargets`): o participante saiu da espera, ou a etapa
   * dele ainda não foi liberada. A copy enuncia a disjunção inteira em vez de
   * escolher uma — escolher seria afirmar causa que a tela não conhece.
   */
  participant_not_resendable: {
    title: 'Este participante não pode receber o convite agora',
    description:
      'Ou ele já saiu da espera (assinou, recusou ou o convite venceu), ou a etapa dele ainda não foi liberada. Recarregue a página para ver o estado atual.',
  },
  /** 503 do reenvio: o controle de cota não respondeu e NADA foi enviado —
   * fail-closed do backend. Dizer "tente de novo" aqui é correto, e é
   * importante afirmar que nenhum e-mail saiu. */
  resend_rate_limit_unavailable: {
    title: 'Reenvio indisponível neste momento',
    description:
      'O controle de reenvios não respondeu e nenhum convite foi enviado. Tente novamente em alguns instantes.',
  },
  /**
   * 400 da criação: `expires_at` divergiu do preset revisado além da tolerância
   * de 10 minutos do backend. Com o prazo calculado no disparo isto virou caso
   * raro (relógio local muito fora de sincronia, ou envio lentíssimo) — mas
   * quando acontece a mensagem precisa dizer o que fazer, não só que falhou.
   */
  deadline_preset_mismatch: {
    title: 'O prazo revisado não confere com o servidor',
    description:
      'O prazo é contado a partir do disparo. Isso acontece quando o relógio deste computador está distante do relógio do servidor, ou quando o envio demorou demais. Volte ao passo Prazo, escolha o prazo de novo e dispare em seguida.',
  },
  rate_limited: {
    title: 'Muitas tentativas em pouco tempo',
    description: 'Aguarde alguns instantes e tente novamente.',
  },
  /**
   * 500 do detalhe (R-UX02-P02/L3B-B) — cardinalidade de documento inesperada
   * (mais de um documento associado ao envio, o que não deveria acontecer).
   * Copy PRÓPRIA e acionável: nunca reinterpretada como "sem documento"
   * (`document: null`), que é um estado normal e diferente deste.
   */
  data_inconsistent: {
    title: 'Este envio está com um problema de dados',
    description:
      'O servidor encontrou um estado inesperado nos documentos deste envio. Isto não é o mesmo que "sem documento associado" — fale com o suporte antes de agir sobre ele.',
  },
  document_too_large: {
    title: 'Arquivo grande demais',
    description: 'Envie um PDF menor e tente de novo.',
  },
  evidence_not_available: {
    title: 'Evidências ainda não disponíveis',
    description: 'O pacote de evidências é gerado quando o documento é concluído.',
  },
  evidence_purged: {
    title: 'Evidência já foi destruída',
    description:
      'O prazo de guarda deste documento foi cumprido e a evidência não está mais disponível.',
  },
  evidence_object_unavailable: {
    title: 'Evidência temporariamente indisponível',
    description: 'O armazenamento não respondeu agora. Tente novamente em alguns instantes.',
  },
  evidence_conflict: {
    title: 'Evidência em um estado inesperado',
    description:
      'O pacote não pôde ser entregue neste momento. Tente novamente; se persistir, fale com o suporte.',
  },
  evidence_too_large: {
    title: 'Pacote de evidências grande demais para entregar agora',
    description:
      'Este pacote passou do limite de entrega direta. Fale com o suporte para receber por outro caminho.',
  },
};

/** Último recurso: erro sem `{ error }` conhecido. Explicita que é um caso não
 * previsto em vez de inventar uma causa. */
export const API_ERROR_FALLBACK = {
  title: 'Não foi possível concluir a ação',
  description: 'Tente novamente em instantes. Se continuar, fale com o suporte.',
} as const;

/** Resposta que não bate com o contrato — categoria própria, nunca confundida
 * com "erro de rede" nem com "dado inválido do usuário". */
export const CONTRACT_VIOLATION_COPY = {
  title: 'O servidor respondeu algo que este aplicativo não entende',
  description:
    'Isso costuma acontecer quando o aplicativo está desatualizado em relação ao servidor. Recarregue a página; se continuar, fale com o suporte antes de agir sobre este documento.',
} as const;

export const SESSION_EXPIRED_COPY = {
  title: 'Sua sessão expirou',
  description: 'Entre de novo para continuar de onde parou.',
} as const;

export interface DescribedApiError {
  title: string;
  description: string;
  code: string | null;
}

/**
 * Traduz qualquer erro do cliente HTTP em título + descrição em português.
 * Nunca joga a mensagem crua do servidor na tela e nunca inventa causa: código
 * desconhecido cai no fallback declarado, com o código preservado em `code`
 * para diagnóstico.
 *
 * **É esta função que as páginas chamam** — nenhuma tela escreve de novo o
 * motivo de um erro. Escrever à mão foi o que fez `401` (sessão expirada) e
 * resposta fora do contrato serem anunciados como "documento removido ou sem
 * acesso": uma causa inventada, e das piores, porque some justamente o aviso de
 * "fale com o suporte antes de agir sobre este documento".
 *
 * `fallbackTitle` cobre o único pedaço que a copy central não pode saber: QUAL
 * ação da tela falhou ("carregar seus documentos", "carregar este documento").
 * Ele só é usado quando o erro NÃO se identifica — nunca sobrepõe a copy de um
 * código conhecido — e não é afirmação de causa, é o que a página estava
 * fazendo. A descrição continua sendo a do fallback, que não inventa motivo.
 */
export function describeApiError(error: unknown, fallbackTitle?: string): DescribedApiError {
  const fallback: DescribedApiError = {
    title: fallbackTitle ?? API_ERROR_FALLBACK.title,
    description: API_ERROR_FALLBACK.description,
    code: null,
  };
  if (error instanceof ContractViolationError) {
    return { ...CONTRACT_VIOLATION_COPY, code: 'contract_violation' };
  }
  if (error instanceof ApiError) {
    if (error.status === 401) return { ...SESSION_EXPIRED_COPY, code: error.code ?? 'unauthorized' };
    const code = error.code;
    const known = code ? API_ERROR_COPY[code] : undefined;
    if (known) return { ...known, code };
    return { ...fallback, code };
  }
  return fallback;
}

export const ENVELOPE_STATUS_FILTER_ALL_LABEL = 'Todos os status';

/** Opções do filtro do painel, derivadas do enum REAL — nunca uma lista escrita
 * à mão (era daí que vinha o filtro por `active`, status que não existe). */
export const ENVELOPE_STATUS_FILTER_OPTIONS: readonly { value: EnvelopeStatus; label: string }[] =
  ENVELOPE_STATUS_VALUES.map((status) => ({ value: status, label: ENVELOPE_STATUS_LABEL[status] }));

export const PARTICIPANT_STATUS_ORDER: readonly ParticipantStatus[] = PARTICIPANT_STATUS_VALUES;
